import { cloneDeep, isEqual } from "lodash";
import { useEffect, useMemo, useState } from "react";
import * as client from "../../../client";
import { UpdateReport } from "../../../api/routes";
import { ClientId } from "../../../client-features";
import { applyOverridesToCompanyInfo } from "../Conversion";
import { EMPTY_POLICY_INFO } from "../general-input/policyInfoOptions";
import {
  companyInfoWithEdits,
  policyInfoWithEditedFields,
} from "../general-input/util";

export type FormSelection = NonNullable<client.PolicyInfo["form_selection"]>;
export type Perils = client.PolicyInfo["included_perils"];

/** The layer's per-policy term overrides. A null term follows the report. */
export type LayerTermOverrides = {
  perils: Perils | null;
  formSelection: FormSelection | null;
  commission: number | null;
};

/**
 * Everything the submission pane edits, in the shape the editors work with:
 * company info with overrides applied, and the report subtrees the excerpt
 * helpers patch. Saving diffs a draft against the one built from the saved
 * report (``draftFromReport``) and sends only what changed.
 */
export type SubmissionDraft = {
  companyInfo: client.RawCompanyInfo;
  policyInfo: client.PolicyInfo;
  layerTerms: LayerTermOverrides;
  limitInfo: client.AdvancedLimitInfo | null;
  deductibleInfo: client.AdvancedDeductibleInfo | null;
  sublimitInfo: client.SublimitInfo | null;
};

export const layerTermOverrides = (
  layer: client.Layer | undefined
): LayerTermOverrides => ({
  perils: layer?.included_perils_override ?? null,
  formSelection: layer?.form_selection_override ?? null,
  commission: layer?.broker_commission_percentage_override ?? null,
});

export const draftFromReport = (
  report: client.CommercialReport,
  layer: client.Layer | undefined,
  clientId: ClientId
): SubmissionDraft => ({
  companyInfo: applyOverridesToCompanyInfo(report.company_info, clientId),
  policyInfo: cloneDeep(report.policy_info ?? EMPTY_POLICY_INFO),
  layerTerms: layerTermOverrides(layer),
  limitInfo: cloneDeep(report.limit_info ?? null),
  deductibleInfo: cloneDeep(report.deductible_info ?? null),
  sublimitInfo: cloneDeep(report.sublimit_info ?? null),
});

export type SubmissionChanges = {
  company: string[];
  policy: string[];
  layerTerms: boolean;
  limits: boolean;
  deductibles: boolean;
  sublimits: boolean;
};

const changedKeys = <T extends object>(a: T, b: T): string[] => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...keys].filter(
    (key) => !isEqual(a[key as keyof T], b[key as keyof T])
  );
};

export const submissionChanges = (
  original: SubmissionDraft,
  draft: SubmissionDraft
): SubmissionChanges => ({
  company: changedKeys(original.companyInfo, draft.companyInfo),
  policy: changedKeys(original.policyInfo, draft.policyInfo),
  layerTerms: !isEqual(original.layerTerms, draft.layerTerms),
  limits: !isEqual(original.limitInfo, draft.limitInfo),
  deductibles: !isEqual(original.deductibleInfo, draft.deductibleInfo),
  sublimits: !isEqual(original.sublimitInfo, draft.sublimitInfo),
});

export const hasChanges = (changes: SubmissionChanges): boolean =>
  changes.company.length > 0 ||
  changes.policy.length > 0 ||
  changes.layerTerms ||
  changes.limits ||
  changes.deductibles ||
  changes.sublimits;

/**
 * The report update that persists a draft, or null when nothing changed. Uses
 * the same write paths as the General, Layers and Policy Fields tabs, so an
 * edit here is indistinguishable from one made there.
 */
export const buildSubmissionUpdate = (
  report: client.CommercialReport,
  layer: client.Layer | undefined,
  original: SubmissionDraft,
  draft: SubmissionDraft
): UpdateReport | null => {
  const changes = submissionChanges(original, draft);
  const update: UpdateReport = {};
  if (changes.company.length > 0) {
    update.companyInfo = companyInfoWithEdits(
      report.company_info,
      original.companyInfo,
      draft.companyInfo
    );
  }
  if (changes.policy.length > 0) {
    update.policyInfo = policyInfoWithEditedFields(
      draft.policyInfo,
      report.policy_info
    );
  }
  if (changes.layerTerms && layer) {
    update.layerInfo = report.layer_info.map((existing) =>
      existing.layer_id === layer.layer_id
        ? {
            ...existing,
            included_perils_override: draft.layerTerms.perils,
            form_selection_override: draft.layerTerms.formSelection,
            broker_commission_percentage_override: draft.layerTerms.commission,
          }
        : existing
    );
  }
  if (changes.limits && draft.limitInfo) {
    update.advancedLimitInfo = draft.limitInfo;
  }
  if (changes.deductibles && draft.deductibleInfo) {
    update.deductibleInfo = draft.deductibleInfo;
  }
  if (changes.sublimits && draft.sublimitInfo) {
    update.sublimitInfo = draft.sublimitInfo;
  }
  // Limit and deductible excerpts feed function generation, so their edits
  // rerun the report exactly as the Policy Fields tab does.
  if (changes.limits || changes.deductibles) update.rerunReport = true;
  return Object.keys(update).length > 0 ? update : null;
};

export type SubmissionDraftState = {
  draft: SubmissionDraft | null;
  changes: SubmissionChanges | null;
  dirty: boolean;
  /** Field-level edits for the save bar; excerpt edits count one per action. */
  editCount: number;
  update: (edit: (draft: SubmissionDraft) => SubmissionDraft) => void;
  updateExcerpts: (edit: (draft: SubmissionDraft) => SubmissionDraft) => void;
  reset: () => void;
  buildUpdate: () => UpdateReport | null;
};

/**
 * Draft state for the submission pane, rebuilt from the report whenever the
 * saved report changes (initial load, a save, a change from another tab).
 */
export const useSubmissionDraft = (
  report: client.ReportResponse | undefined,
  layer: client.Layer | undefined,
  clientId: ClientId
): SubmissionDraftState => {
  const original = useMemo(
    () =>
      report ? draftFromReport(report.report_json, layer, clientId) : null,
    [report, layer, clientId]
  );
  // Keyed on a JSON snapshot so a refetch that changed nothing (or a local
  // edit) never wipes the draft.
  const originalKey = original ? JSON.stringify(original) : "";
  const [draft, setDraft] = useState<SubmissionDraft | null>(null);
  const [excerptEdits, setExcerptEdits] = useState(0);

  useEffect(() => {
    setDraft(original ? cloneDeep(original) : null);
    setExcerptEdits(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the snapshot, see above
  }, [originalKey]);

  const changes = original && draft ? submissionChanges(original, draft) : null;
  const dirty = changes ? hasChanges(changes) : false;
  const editCount = changes
    ? changes.company.length +
      changes.policy.length +
      (changes.layerTerms ? 1 : 0) +
      excerptEdits
    : 0;

  const update = (edit: (draft: SubmissionDraft) => SubmissionDraft) =>
    setDraft((current) => (current ? edit(cloneDeep(current)) : current));

  return {
    draft,
    changes,
    dirty,
    editCount,
    update,
    updateExcerpts: (edit) => {
      update(edit);
      setExcerptEdits((count) => count + 1);
    },
    reset: () => {
      setDraft(original ? cloneDeep(original) : null);
      setExcerptEdits(0);
    },
    buildUpdate: () =>
      report && original && draft
        ? buildSubmissionUpdate(report.report_json, layer, original, draft)
        : null,
  };
};
